import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { fetchExploreSearch } from "../../api/heatmapApi";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { enumLabel } from "../../utils/enumLabels";
import { isPlausibleYield } from "../../lib/yieldPlausibility";

// V4.12 Phase C — vertical-aware sort options (auto + immo).
const SORTS_BY_VERTICAL = {
  immo: ["best_deals", "yield", "price_asc", "newest", "area", "confidence"],
  auto: ["best_deals", "price_asc", "newest", "mileage", "year", "confidence"],
};
const PAGE_SIZE = 20;

// Welle 5 — "Als Liste öffnen": renders the CURRENT viewport + filters
// as a server-paginated list. `baseParams` is exactly the object the
// map used — identical query by construction.
export const ExploreListPanel = ({ open, onClose, baseParams, initialTotal, vertical = "immo", t }) => {
  const navigate = useNavigate();
  const SORTS = SORTS_BY_VERTICAL[vertical] || SORTS_BY_VERTICAL.immo;
  const [sortRaw, setSort] = useState("best_deals");
  const sort = SORTS.includes(sortRaw) ? sortRaw : "best_deals";
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const keyRef = useRef(null);
  const paramsKey = JSON.stringify(baseParams);
  // Bug D: while the list overlay is open the body (and the map behind
  // it) must never scroll — full scroll isolation.
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open || !baseParams?.bbox) return;
    // UX-Fix — exactly ONE request per param/sort change: while page > 1,
    // reset to page 1 first and fetch on the re-run (no stale-page fetch).
    const key = `${paramsKey}|${sort}`;
    if (keyRef.current !== key) {
      keyRef.current = key;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    fetchExploreSearch(
      { ...baseParams, mode: "list", sort, page, page_size: PAGE_SIZE },
      { signal: controller.signal },
    )
      .then((d) => setData(d))
      .catch((err) => {
        if (err?.code !== "ERR_CANCELED" && err?.name !== "CanceledError") {
          setData(null);
        }
      })
      .finally(() => {
        if (abortRef.current === controller) setLoading(false);
      });
    // abort in-flight request on close/unmount/dep change (also keeps
    // React 18 StrictMode double-invoke down to a single live request)
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paramsKey, sort, page]);

  if (!open) return null;
  // Count stays visible while the list body loads: fall back to the
  // map's viewport total (identical query) until the list responds.
  const total = data?.total_count ?? initialTotal ?? 0;
  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showCount = data != null || (initialTotal ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-[1200] flex justify-end bg-zinc-950/60">
      <div
        data-testid="explore-list-panel"
        className="flex h-full w-full flex-col border-l border-zinc-800 bg-zinc-950 md:w-[440px]"
      >
        <div className="flex items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-zinc-100" data-testid="explore-list-total">
              {showCount
                ? t(
                    vertical === "auto"
                      ? "explore.matchCountAuto"
                      : "explore.matchCount",
                    { count: total },
                  )
                : t("explore.countLoading")}
            </div>
            <div className="text-[11px] text-zinc-500">
              {vertical === "immo" && (
                <>
                  {t(`explore.${baseParams.transaction_type === "rent" ? "rent" : "buy"}`)}
                  {" · "}
                </>
              )}
              {t(`positions.${baseParams.market_position}`, { defaultValue: baseParams.market_position })}
            </div>
          </div>
          <button
            type="button"
            data-testid="explore-list-close"
            onClick={onClose}
            className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-2">
          <span className="text-xs text-zinc-500">{t("explore.sortLabel")}</span>
          <select
            data-testid="explore-sort"
            value={sort}
            disabled={loading || !data}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100"
          >
            {SORTS.map((s) => (
              <option key={s} value={s}>{t(`explore.sort.${s}`)}</option>
            ))}
          </select>
          {loading && <Loader2 className="ml-auto h-4 w-4 animate-spin text-amber-400" />}
        </div>

        <div
          className="flex-1 overflow-y-auto overscroll-contain"
          style={{ touchAction: "pan-y" }}
        >
          {loading && !data && (
            <div data-testid="explore-list-skeleton" className="space-y-3 px-4 py-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="h-16 w-20 flex-none rounded-lg bg-zinc-900" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-3/4 rounded bg-zinc-900" />
                    <div className="h-3 w-1/2 rounded bg-zinc-900" />
                  </div>
                </div>
              ))}
              <div className="pt-2 text-center text-xs text-zinc-500">
                {t("explore.countLoading")}
              </div>
            </div>
          )}
          {!loading && data && (data.items || []).length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="explore-list-empty">
              {t(vertical === "auto" ? "explore.noResultsAuto" : "explore.noResults")}
            </div>
          )}
          {(data?.items || []).map((it) => (
            <button
              key={it.ulid}
              type="button"
              data-testid={`explore-list-item-${it.ulid}`}
              onClick={() => navigate(`/listing/${it.ulid}`)}
              className="flex w-full items-center gap-3 border-b border-zinc-900 px-4 py-3 text-left transition-colors hover:bg-zinc-900/60"
            >
              {it.primary_image_url ? (
                <img
                  src={it.primary_image_url}
                  alt=""
                  loading="lazy"
                  className="h-16 w-20 flex-none rounded-lg object-cover"
                />
              ) : (
                <div className="h-16 w-20 flex-none rounded-lg bg-zinc-900" />
              )}
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="truncate text-[13px] font-medium text-zinc-100">
                  {it.title}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-zinc-100">
                    {typeof it.price === "number"
                      ? `€ ${Math.round(it.price).toLocaleString("de-AT")}`
                      : "—"}
                  </span>
                  {typeof it.price_vs_market_pct === "number" && (
                    <span
                      className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${
                        it.price_vs_market_pct > 0
                          ? "border-orange-500/50 bg-orange-500/15 text-orange-300"
                          : "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                      }`}
                    >
                      {it.price_vs_market_pct > 0 ? "+" : "−"}
                      {Math.abs(Math.round(it.price_vs_market_pct))} %
                    </span>
                  )}
                  {isPlausibleYield(it.est_gross_yield_pct) && (
                    <span className="text-[10px] text-sky-300">
                      {t("explore.yield")} {it.est_gross_yield_pct.toFixed(1)} %
                    </span>
                  )}
                </div>
                <div className="truncate text-[11px] text-zinc-500">
                  {(vertical === "auto"
                    ? [
                        it.year || null,
                        it.mileage_km != null
                          ? `${Math.round(it.mileage_km).toLocaleString("de-AT")} km`
                          : null,
                        it.fuel ? enumLabel("fuel", it.fuel) : null,
                        it.city || it.postal_code || null,
                        it.country || null,
                      ]
                    : [
                        it.living_area_m2 ? `${Math.round(it.living_area_m2)} m²` : null,
                        it.rooms ? `${it.rooms} Zi.` : null,
                        it.city || it.postal_code || null,
                        it.country || null,
                      ]
                  ).filter(Boolean).join(" · ")}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-zinc-800 px-4 py-2.5">
          <button
            type="button"
            data-testid="explore-page-prev"
            disabled={page <= 1 || loading || !data}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="inline-flex items-center gap-1 rounded-lg border border-zinc-800 px-3 py-1.5 text-xs text-zinc-300 disabled:opacity-40"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs text-zinc-500" data-testid="explore-page-indicator">
            {t("explore.page")} {page} / {maxPage}
          </span>
          <button
            type="button"
            data-testid="explore-page-next"
            disabled={page >= maxPage || loading || !data}
            onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
            className="inline-flex items-center gap-1 rounded-lg border border-zinc-800 px-3 py-1.5 text-xs text-zinc-300 disabled:opacity-40"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
